import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  Box,
  Radio,
  RadioGroup,
} from "@mui/material";
import MessageTemplateService from "../services/MessageTemplateService";
import { filterTemplatesForScreen } from "../utils/templateAppUsage";
import QuotationService from "../services/QuotationService";
import {
  buildQuotationVariableDefaults,
  inferTemplateVariableSlots,
} from "../utils/templateVariableSamples";

function hasVariableSlots(slots) {
  return ["header", "body", "footer", "buttons"].some(
    (section) => Object.keys(slots?.[section] || {}).length > 0,
  );
}

function urlSlotKey(section, key) {
  return `${section}:${key}`;
}

function parseUrlSlotKey(value) {
  if (!value) return null;
  const [section, key] = String(value).split(":");
  if (!section || key == null || key === "") return null;
  return { section, key };
}

function collectUrlSlotOptions(slots) {
  const options = [];
  for (const section of ["header", "body", "footer", "buttons"]) {
    const keys = Object.keys(slots?.[section] || {}).sort((a, b) => Number(a) - Number(b));
    keys.forEach((key) => {
      options.push({
        value: urlSlotKey(section, key),
        label: section === "buttons"
          ? `Button ${Number(key) + 1} URL suffix`
          : `${section} {{${key}}}`,
        section,
        key,
      });
    });
  }
  return options;
}

export default function QuotationSendDialog({
  open,
  onClose,
  onSend,
  quotation,
}) {
  const [deliveryType, setDeliveryType] = useState("pdf");
  const [messageType, setMessageType] = useState("template");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [templateVariables, setTemplateVariables] = useState({
    header: {},
    body: {},
    footer: {},
    buttons: {},
  });
  const [urlVariableKey, setUrlVariableKey] = useState("");
  const [messageText, setMessageText] = useState("");
  const [canSendNormal, setCanSendNormal] = useState(false);
  const [windowExpiresAt, setWindowExpiresAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.getId() === templateId) || null,
    [templates, templateId],
  );

  const variableSlots = useMemo(
    () => (selectedTemplate ? inferTemplateVariableSlots(selectedTemplate) : {
      header: {},
      body: {},
      footer: {},
      buttons: {},
    }),
    [selectedTemplate],
  );

  const urlSlotOptions = useMemo(
    () => collectUrlSlotOptions(variableSlots),
    [variableSlots],
  );

  const needsTemplate = deliveryType === "pdf"
    || (deliveryType === "url" && messageType === "template");

  useEffect(() => {
    if (!open) return;
    setError("");
    setDeliveryType("pdf");
    setMessageType("template");
    setTemplateId("");
    setTemplateVariables({ header: {}, body: {}, footer: {}, buttons: {} });
    setUrlVariableKey("");
    setMessageText(
      quotation?.contact_name
        ? `Hi ${quotation.contact_name}, please review your quotation: {{url}}`
        : "Hi, please review your quotation: {{url}}",
    );
    setCanSendNormal(false);
    setWindowExpiresAt(null);
    setLoading(true);

    Promise.all([
      MessageTemplateService.getAllTemplates()
        .then((items) => {
          const approved = filterTemplatesForScreen(items, "quotation");
          setTemplates(approved);
        })
        .catch(() => {
          setTemplates([]);
          setError("Failed to load templates");
        }),
      quotation?.id
        ? QuotationService.getSendOptions(quotation.id)
          .then((data) => {
            setCanSendNormal(Boolean(data?.can_send_normal_message));
            setWindowExpiresAt(data?.window_expires_at || null);
          })
          .catch(() => {
            setCanSendNormal(false);
            setWindowExpiresAt(null);
          })
        : Promise.resolve(),
    ]).finally(() => setLoading(false));
  }, [open, quotation?.id, quotation?.contact_name]);

  useEffect(() => {
    if (!selectedTemplate || !quotation) return;
    setTemplateVariables(buildQuotationVariableDefaults(quotation, variableSlots));
    const options = collectUrlSlotOptions(variableSlots);
    setUrlVariableKey(options[0]?.value || "");
  }, [selectedTemplate, quotation, variableSlots]);

  useEffect(() => {
    if (deliveryType === "url" && messageType === "normal" && !canSendNormal) {
      setMessageType("template");
    }
  }, [deliveryType, messageType, canSendNormal]);

  const validateVariables = ({ skipUrlSlot = false } = {}) => {
    const skip = skipUrlSlot ? parseUrlSlotKey(urlVariableKey) : null;
    for (const section of ["header", "body", "footer", "buttons"]) {
      const slot = variableSlots[section] || {};
      for (const key of Object.keys(slot)) {
        if (skip && skip.section === section && String(skip.key) === String(key)) {
          continue;
        }
        const value = templateVariables?.[section]?.[key];
        if (!value || !String(value).trim()) {
          const label = section === "buttons"
            ? "website button URL suffix"
            : `{{${key}}} in ${section}`;
          setError(`Please enter a value for ${label}`);
          return false;
        }
      }
    }
    return true;
  };

  const handleTemplateChange = (nextTemplateId) => {
    setTemplateId(nextTemplateId);
    setError("");
    const template = templates.find((item) => item.getId() === nextTemplateId);
    if (!template || !quotation) {
      setTemplateVariables({ header: {}, body: {}, footer: {}, buttons: {} });
      setUrlVariableKey("");
      return;
    }
    const slots = inferTemplateVariableSlots(template);
    setTemplateVariables(buildQuotationVariableDefaults(quotation, slots));
    const options = collectUrlSlotOptions(slots);
    setUrlVariableKey(options[0]?.value || "");
  };

  const handleSend = async () => {
    if (deliveryType === "pdf") {
      if (!templateId) {
        setError("Select a template");
        return;
      }
      if (!validateVariables()) return;
    } else if (messageType === "template") {
      if (!templateId) {
        setError("Select a template");
        return;
      }
      if (!hasVariableSlots(variableSlots)) {
        setError("This template has no placeholders for the tracking URL. Choose another template or use a normal message.");
        return;
      }
      if (!urlVariableKey) {
        setError("Select which template variable should receive the tracking URL");
        return;
      }
      if (!validateVariables({ skipUrlSlot: true })) return;
    } else {
      if (!canSendNormal) {
        setError("24-hour messaging window expired. Use a template instead.");
        return;
      }
      if (!String(messageText || "").trim()) {
        setError("Enter a message");
        return;
      }
    }

    setSending(true);
    setError("");
    try {
      await onSend?.({
        deliveryType,
        messageType: deliveryType === "url" ? messageType : undefined,
        templateId: needsTemplate ? templateId : undefined,
        variables: needsTemplate && hasVariableSlots(variableSlots)
          ? templateVariables
          : undefined,
        urlVariable: deliveryType === "url" && messageType === "template"
          ? parseUrlSlotKey(urlVariableKey)
          : undefined,
        messageText: deliveryType === "url" && messageType === "normal"
          ? messageText
          : undefined,
      });
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to send quotation");
    } finally {
      setSending(false);
    }
  };

  const label = quotation?.quotation_label
    || (quotation?.quotation_number ? `QUOT-${quotation.quotation_number}` : "quotation");

  const renderVariableFields = (section, title) => {
    const slot = variableSlots[section] || {};
    const keys = Object.keys(slot);
    if (keys.length === 0) return null;
    return (
      <Box key={section} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {title}
        </Typography>
        {keys.sort((a, b) => Number(a) - Number(b)).map((key) => {
          const isUrlSlot = deliveryType === "url"
            && messageType === "template"
            && urlVariableKey === urlSlotKey(section, key);
          return (
            <TextField
              key={`${section}-${key}`}
              label={section === "buttons" ? `Button ${Number(key) + 1} URL suffix` : `Value for {{${key}}}`}
              value={isUrlSlot ? "(tracking URL)" : (templateVariables?.[section]?.[key] || "")}
              disabled={isUrlSlot}
              onChange={(e) => {
                setError("");
                setTemplateVariables((prev) => ({
                  ...prev,
                  [section]: {
                    ...prev[section],
                    [key]: e.target.value,
                  },
                }));
              }}
              helperText={isUrlSlot
                ? "This placeholder will be replaced with the tracking URL"
                : "Pre-filled from quotation — edit or replace with any text"}
              fullWidth
              size="small"
            />
          );
        })}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Quotation via WhatsApp</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Send to
          {" "}
          <strong>{quotation?.contact_name || "the contact"}</strong>
          {" "}
          (
          {quotation?.contact_phone || "no phone"}
          )
          {" "}
          —
          {" "}
          {label}
        </Typography>

        <FormControl>
          <FormLabel>Delivery</FormLabel>
          <RadioGroup
            row
            value={deliveryType}
            onChange={(e) => {
              setDeliveryType(e.target.value);
              setError("");
            }}
          >
            <FormControlLabel value="pdf" control={<Radio />} label="PDF" />
            <FormControlLabel value="url" control={<Radio />} label="URL" />
          </RadioGroup>
        </FormControl>

        {deliveryType === "pdf" && (
          <Alert severity="info">
            Sends an approved WhatsApp template and attaches the quotation PDF.
          </Alert>
        )}

        {deliveryType === "url" && (
          <>
            <Alert severity="info">
              Sends a tracking link to our server. When the customer opens it, we redirect to the PDF and notify your team.
            </Alert>
            <FormControl>
              <FormLabel>Message type</FormLabel>
              <RadioGroup
                row
                value={messageType}
                onChange={(e) => {
                  setMessageType(e.target.value);
                  setError("");
                }}
              >
                <FormControlLabel value="template" control={<Radio />} label="Template" />
                <FormControlLabel
                  value="normal"
                  control={<Radio />}
                  label="Normal message"
                  disabled={!canSendNormal}
                />
              </RadioGroup>
            </FormControl>
            {!canSendNormal && (
              <Alert severity="warning">
                Normal message is disabled because the 24-hour WhatsApp window
                {windowExpiresAt ? ` expired (was open until ${new Date(windowExpiresAt).toLocaleString()})` : " is closed"}
                . Use a template instead.
              </Alert>
            )}
          </>
        )}

        {loading ? (
          <CircularProgress size={24} sx={{ alignSelf: "center" }} />
        ) : (
          <>
            {needsTemplate && (
              <FormControl fullWidth>
                <InputLabel>WhatsApp Template</InputLabel>
                <Select
                  label="WhatsApp Template"
                  value={templateId}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                >
                  {templates.map((template) => (
                    <MenuItem key={template.getId()} value={template.getId()}>
                      {template.getTemplateName()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {needsTemplate && !loading && templates.length === 0 && (
              <Alert severity="warning">
                No approved Meta templates found. Create and sync a template in Broadcast → Templates first.
              </Alert>
            )}

            {needsTemplate && selectedTemplate && hasVariableSlots(variableSlots) && (
              <Alert severity="info">
                Placeholders are pre-filled from the quotation when possible.
                {deliveryType === "url" && messageType === "template"
                  ? " Choose which variable should receive the tracking URL."
                  : " You can keep them, edit them, or replace them with any text."}
              </Alert>
            )}

            {deliveryType === "url" && messageType === "template" && urlSlotOptions.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Tracking URL variable</InputLabel>
                <Select
                  label="Tracking URL variable"
                  value={urlVariableKey}
                  onChange={(e) => {
                    setUrlVariableKey(e.target.value);
                    setError("");
                  }}
                >
                  {urlSlotOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {needsTemplate && (
              <>
                {renderVariableFields("header", "Header variables")}
                {renderVariableFields("body", "Body variables")}
                {renderVariableFields("footer", "Footer variables")}
                {renderVariableFields("buttons", "Button variables")}
              </>
            )}

            {deliveryType === "url" && messageType === "normal" && (
              <TextField
                label="Message"
                value={messageText}
                onChange={(e) => {
                  setMessageText(e.target.value);
                  setError("");
                }}
                helperText="Use {{url}} where the tracking link should appear. If omitted, the link is appended."
                fullWidth
                multiline
                minRows={4}
              />
            )}
          </>
        )}

        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={
            sending
            || loading
            || (needsTemplate && !templateId)
            || (deliveryType === "url" && messageType === "normal" && !canSendNormal)
          }
        >
          {sending ? "Sending..." : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
